import React, { useState, useEffect } from 'react';
import { BRAND } from '../config/branding';
import axiosClient from '../api/axiosClient';
import { isoStandardsService } from '../api/isoStandardsService';

function SLAManagement() {
  const [slas, setSlas] = useState({
    provider: [],
    audit: [],
    accreditation: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('provider');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [certBodies, setCertBodies] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedSLA, setSelectedSLA] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pricingSettings, setPricingSettings] = useState(null);
  const [deletingSLA, setDeletingSLA] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [selectedVersionSLA, setSelectedVersionSLA] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isoStandards, setIsoStandards] = useState([]);

  // Form data for creating SLAs
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSLAs();
    fetchCertBodies();
    fetchClients();
    fetchPricingSettings();
    fetchISOStandards();
  }, []);

  const fetchISOStandards = async () => {
    try {
      const response = await isoStandardsService.getPurchased();
      if (response.data && Array.isArray(response.data)) {
        setIsoStandards(response.data);
      }
    } catch (err) {
      console.error('Error fetching ISO standards:', err);
    }
  };

  const fetchPricingSettings = async () => {
    try {
      const res = await axiosClient.get('/sla-settings/pricing');
      setPricingSettings(res.data);
    } catch (err) {
      console.error('Error fetching pricing settings:', err);
    }
  };

  const fetchSLAs = async () => {
    try {
      setLoading(true);
      // Fetch different types of SLAs
      const [providerRes, auditRes, accreditationRes] = await Promise.all([
        axiosClient.get('/sla-management/provider-slas').catch(() => ({ data: [] })),
        axiosClient.get('/sla-management/audit-service-slas').catch(() => ({ data: [] })),
        axiosClient.get('/sla-management/accreditation-slas').catch(() => ({ data: [] }))
      ]);

      setSlas({
        provider: providerRes.data,
        audit: auditRes.data,
        accreditation: accreditationRes.data
      });
    } catch (err) {
      console.error('Error fetching SLAs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertBodies = async () => {
    try {
      const res = await axiosClient.get('/cert-bodies/list');
      setCertBodies(res.data || []);
    } catch (err) {
      console.error('Error fetching cert bodies:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axiosClient.get('/companies');
      setClients(res.data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const openCreateModal = (type) => {
    setActiveTab(type);
    setError('');
    setSuccess('');

    // Set default form data based on type
    if (type === 'provider') {
      setFormData({
        title: 'ICSC Service Level Agreement',
        description: 'Service agreement between ICSC and Certification Body for platform access, support, and certification management services',
        system_uptime_percentage: 99.9,
        support_response_time_hours: 24,
        support_hours: '24/7',
        critical_issue_resolution_hours: 48,
        feature_request_processing_days: 30,
        max_scheduled_downtime_hours_per_month: 2,
        maintenance_window: 'Sunday 2-4 AM UTC',
        effective_date: new Date().toISOString(),
        expiration_date: ''
      });
    } else if (type === 'audit') {
      setFormData({
        title: 'Reciprocal Audit Services Agreement',
        description: 'Reciprocal agreement between certification bodies for impartial cross-audit services of clients',
        iso_standard: 'ISO 9001',
        audit_plan_delivery_days: 5,
        pre_audit_response_hours: 24,
        main_audit_duration_days: 3,
        report_delivery_days: 10,
        query_response_time_hours: 8,
        non_conformity_guidance_days: 14,
        auditor_qualification_level: 'ISO IEC 17021 Certified',
        audit_repeatability_commitment: 'Same auditor for follow-up audits where possible',
        effective_date: new Date().toISOString(),
        expiration_date: ''
      });
    } else if (type === 'accreditation') {
      setFormData({
        title: `${BRAND.org} Agreement`,
        description: 'Accreditation agreement defining certification body scope for ISO standards, CPD certification, and company assessments',
        accreditation_duration_days: 1095,
        audit_frequency_months: 12,
        initial_assessment_days: 10,
        non_conformity_response_days: 30,
        renewal_notice_days: 90,
        iso_standards_accredited: [],  // Array of selected ISO standards
        includes_cpd_certification: false,
        includes_company_assessments: false,
        compliance_obligations: 'Maintain staff training and yearly competency assessment',
        annual_fee: 0,  // Will be auto-calculated
        fee_payment_terms: 'Annual payment in advance',
        suspension_terms: 'Suspension if 3+ non-conformities within 12 months',
        effective_date: new Date().toISOString(),
        expiration_date: ''
      });
    }

    setShowCreateModal(true);
  };

  // Auto-calculate annual fee based on selected services
  const calculateAnnualFee = (isoStandards, includeCpd, includeAssessments) => {
    if (!pricingSettings) return 0;

    let totalFee = 0;
    const isoCount = isoStandards?.length || 0;

    // Calculate ISO standards fee
    if (isoCount > 0) {
      if (isoCount <= 3) {
        // £500 per standard for first 3
        totalFee += isoCount * pricingSettings.iso_price_per_standard_first_3;
      } else {
        // Flat rate for 4-10 ISOs
        totalFee += pricingSettings.iso_price_4_to_10_total;
      }
    }

    // Add CPD certification fee
    if (includeCpd) {
      totalFee += pricingSettings.cpd_certification_annual_fee;
    }

    // Add company assessments fee
    if (includeAssessments) {
      totalFee += pricingSettings.company_assessments_annual_fee;
    }

    return totalFee;
  };

  // Toggle ISO standard selection
  const toggleISOStandard = (standard) => {
    const currentStandards = formData.iso_standards_accredited || [];
    let newStandards;

    if (currentStandards.includes(standard)) {
      newStandards = currentStandards.filter(s => s !== standard);
    } else {
      newStandards = [...currentStandards, standard];
    }

    const newFee = calculateAnnualFee(
      newStandards,
      formData.includes_cpd_certification,
      formData.includes_company_assessments
    );

    setFormData({
      ...formData,
      iso_standards_accredited: newStandards,
      annual_fee: newFee
    });
  };

  // Toggle CPD certification
  const toggleCPD = () => {
    const newInclude = !formData.includes_cpd_certification;
    const newFee = calculateAnnualFee(
      formData.iso_standards_accredited,
      newInclude,
      formData.includes_company_assessments
    );

    setFormData({
      ...formData,
      includes_cpd_certification: newInclude,
      annual_fee: newFee
    });
  };

  // Toggle company assessments
  const toggleCompanyAssessments = () => {
    const newInclude = !formData.includes_company_assessments;
    const newFee = calculateAnnualFee(
      formData.iso_standards_accredited,
      formData.includes_cpd_certification,
      newInclude
    );

    setFormData({
      ...formData,
      includes_company_assessments: newInclude,
      annual_fee: newFee
    });
  };

  const handleCreateSLA = async () => {
    try {
      setCreating(true);
      setError('');
      setSuccess('');

      let endpoint = '';
      let payload = { ...formData };

      // Convert date strings to ISO datetime format
      if (payload.effective_date && !payload.effective_date.includes('T')) {
        payload.effective_date = new Date(payload.effective_date).toISOString();
      }
      if (payload.expiration_date && !payload.expiration_date.includes('T')) {
        payload.expiration_date = new Date(payload.expiration_date).toISOString();
      }

      if (activeTab === 'provider') {
        endpoint = '/sla-management/provider-slas';
        payload.cert_body_id = parseInt(formData.cert_body_id);
      } else if (activeTab === 'audit') {
        endpoint = '/sla-management/audit-service-slas';
        payload.cert_body_id = parseInt(formData.cert_body_id);
        payload.client_company_id = parseInt(formData.client_company_id);
      } else if (activeTab === 'accreditation') {
        endpoint = '/sla-management/accreditation-slas';
        payload.cert_body_id = parseInt(formData.cert_body_id);
      }

      await axiosClient.post(endpoint, payload);
      setSuccess('SLA created successfully!');
      setShowCreateModal(false);
      fetchSLAs();
    } catch (err) {
      // Handle Pydantic validation errors properly
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Pydantic returns an array of error objects for validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(e => `${e.loc.join(' → ')}: ${e.msg}`).join('; ');
          setError(errorMessages);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Validation error occurred');
        }
      } else {
        setError(err.message || 'Failed to create SLA');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleSignSLA = async (slaId, type) => {
    try {
      let endpoint = '';
      if (type === 'provider') {
        endpoint = `/sla-management/provider-slas/${slaId}/sign`;
      } else if (type === 'audit') {
        endpoint = `/sla-management/audit-service-slas/${slaId}/sign`;
      } else if (type === 'accreditation') {
        endpoint = `/sla-management/accreditation-slas/${slaId}/sign`;
      }

      await axiosClient.post(endpoint);
      setSuccess('SLA signed successfully!');
      fetchSLAs();
    } catch (err) {
      // Handle Pydantic validation errors properly
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Pydantic returns an array of error objects for validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(e => `${e.loc.join(' → ')}: ${e.msg}`).join('; ');
          setError(errorMessages);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Validation error occurred');
        }
      } else {
        setError(err.message || 'Failed to sign SLA');
      }
    }
  };

  const handleDeleteSLA = async () => {
    if (!deletingSLA) return;

    try {
      let endpoint = '';
      const { id, type } = deletingSLA;

      if (type === 'provider') {
        endpoint = `/sla-management/provider-slas/${id}`;
      } else if (type === 'audit') {
        endpoint = `/sla-management/audit-service-slas/${id}`;
      } else if (type === 'accreditation') {
        endpoint = `/sla-management/accreditation-slas/${id}`;
      }

      await axiosClient.delete(endpoint);
      setSuccess('SLA deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingSLA(null);
      fetchSLAs();
    } catch (err) {
      // Handle Pydantic validation errors properly
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(e => `${e.loc.join(' → ')}: ${e.msg}`).join('; ');
          setError(errorMessages);
        } else if (typeof detail === 'string') {
          setError(detail);
        } else {
          setError('Deletion error occurred');
        }
      } else {
        setError(err.message || 'Failed to delete SLA');
      }
      setShowDeleteConfirm(false);
      setDeletingSLA(null);
    }
  };

  const openDeleteConfirm = (slaId, type) => {
    setDeletingSLA({ id: slaId, type });
    setShowDeleteConfirm(true);
  };

  const openVersionHistory = async (sla, type) => {
    setSelectedVersionSLA({ sla, type });
    setShowVersionHistory(true);
    setLoadingVersions(true);

    try {
      const response = await axiosClient.get(`/sla-management/versions/${type}/${sla.id}`);
      setVersionHistory(response.data);
    } catch (err) {
      console.error('Error fetching version history:', err);
      setError('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const viewVersion = (version) => {
    setSelectedVersion(version);
  };

  const closeVersionHistory = () => {
    setShowVersionHistory(false);
    setVersionHistory([]);
    setSelectedVersionSLA(null);
    setSelectedVersion(null);
  };

  const renderSLATable = (slaList, type) => {
    if (slaList.length === 0) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No {type} SLAs found. Click "Create {type.charAt(0).toUpperCase() + type.slice(1)} SLA" to add one.
        </div>
      );
    }

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
            <th style={{ padding: '12px', textAlign: 'left' }}>Title</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Effective Date</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Expiration Date</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Signatures</th>
            <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {slaList.map((sla) => {
            const isExpired = sla.expiration_date && new Date(sla.expiration_date) < new Date();
            const isActive = sla.is_active && !isExpired;

            // Determine signature status based on SLA type
            let signatures = [];
            let canSign = false;

            if (type === 'provider') {
              if (sla.signed_by_cert_admin_id) signatures.push('Cert Body');
              if (sla.signed_by_iso_boss_admin_id) signatures.push(BRAND.short);
              canSign = !sla.signed_by_cert_admin_id || !sla.signed_by_iso_boss_admin_id;
            } else if (type === 'audit') {
              if (sla.signed_by_cert_admin_id) signatures.push('Cert Body');
              if (sla.signed_by_client_admin_id) signatures.push('Client');
              canSign = !sla.signed_by_cert_admin_id || !sla.signed_by_client_admin_id;
            } else if (type === 'accreditation') {
              if (sla.signed_by_cert_admin_id) signatures.push('Cert Body');
              if (sla.signed_by_soundbridge_admin_id) signatures.push('ICSC');
              canSign = !sla.signed_by_cert_admin_id || !sla.signed_by_soundbridge_admin_id;
            }

            const signatureText = signatures.length > 0
              ? signatures.join(' + ') + (sla.signed_at ? ` (${new Date(sla.signed_at).toLocaleDateString()})` : '')
              : 'Pending';

            return (
              <tr key={sla.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '12px' }}>{sla.title}</td>
                <td style={{ padding: '12px' }}>
                  {new Date(sla.effective_date).toLocaleDateString()}
                </td>
                <td style={{ padding: '12px' }}>
                  {sla.expiration_date ? new Date(sla.expiration_date).toLocaleDateString() : 'N/A'}
                </td>
                <td style={{ padding: '12px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: isActive ? '#d4edda' : '#f8d7da',
                    color: isActive ? '#155724' : '#721c24',
                    fontSize: '12px'
                  }}>
                    {isExpired ? 'Expired' : isActive ? 'Active' : 'Pending Signature'}
                  </span>
                </td>
                <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                  {signatureText}
                </td>
                <td style={{ padding: '12px' }}>
                  {canSign && !isExpired && (
                    <button
                      onClick={() => handleSignSLA(sla.id, type)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '8px'
                      }}
                    >
                      Sign SLA
                    </button>
                  )}
                  {isActive && (
                    <span style={{ color: '#28a745', fontSize: '12px', marginRight: '8px' }}>Fully Executed</span>
                  )}
                  <button
                    onClick={() => openVersionHistory(sla, type)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      marginRight: '8px'
                    }}
                  >
                    History
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(sla.id, type)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h2>Create {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} SLA</h2>

          {error && (
            <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Certification Body *
            </label>
            <select
              value={formData.cert_body_id || ''}
              onChange={(e) => setFormData({ ...formData, cert_body_id: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              required
            >
              <option value="">Select Certification Body</option>
              {certBodies.map(cb => (
                <option key={cb.id} value={cb.id}>
                  {cb.name} {cb.accreditation_number ? `(${cb.accreditation_number})` : ''}
                </option>
              ))}
            </select>
          </div>

          {activeTab === 'audit' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Partner Certification Body * (Reciprocal Auditing)
              </label>
              <select
                value={formData.client_company_id || ''}
                onChange={(e) => setFormData({ ...formData, client_company_id: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                required
              >
                <option value="">Select Partner Certification Body</option>
                {certBodies.map(cb => (
                  <option key={cb.id} value={cb.id}>
                    {cb.name} {cb.accreditation_number ? `(${cb.accreditation_number})` : ''}
                  </option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginBottom: 0 }}>
                This agreement allows both certification bodies to audit each other's clients impartially
              </p>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title *</label>
            <input
              type="text"
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }}
            />
          </div>

          {/* Provider SLA Specific Fields */}
          {activeTab === 'provider' && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <h3 style={{ fontSize: '14px', marginBottom: '10px', color: '#333' }}>ICSC Services Included:</h3>
                <ul style={{ fontSize: '13px', color: '#666', lineHeight: '1.8', paddingLeft: '20px' }}>
                  <li>24-hour support response time</li>
                  <li>Backup certifications</li>
                  <li>24/7 access to {BRAND.name} platform</li>
                  <li>Certification creation and management</li>
                  <li>Company accreditation</li>
                  <li>CPD training courses (equipment not included)</li>
                  <li>Company assessment audit guidance</li>
                  <li>Control and management of all records</li>
                </ul>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' , gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Platform Uptime %</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.system_uptime_percentage || ''}
                    onChange={(e) => setFormData({ ...formData, system_uptime_percentage: parseFloat(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Support Availability</label>
                  <input
                    type="text"
                    value={formData.support_hours || ''}
                    onChange={(e) => setFormData({ ...formData, support_hours: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Support Response Time (hrs)</label>
                  <input
                    type="number"
                    value={formData.support_response_time_hours || ''}
                    onChange={(e) => setFormData({ ...formData, support_response_time_hours: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Critical Issue Resolution (hrs)</label>
                  <input
                    type="number"
                    value={formData.critical_issue_resolution_hours || ''}
                    onChange={(e) => setFormData({ ...formData, critical_issue_resolution_hours: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Audit SLA Specific Fields */}
          {activeTab === 'audit' && (
            <>
              <div style={{ backgroundColor: '#fff3cd', padding: '10px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #ffc107' }}>
                <p style={{ fontSize: '12px', color: '#856404', margin: 0, fontWeight: 'bold' }}>
                   Impartiality Notice
                </p>
                <p style={{ fontSize: '12px', color: '#856404', margin: '5px 0 0 0' }}>
                  This reciprocal agreement ensures impartial auditing. There must be no influence between companies regarding client audit outcomes or success rates. All audits must be conducted independently and objectively.
                </p>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>ISO Standard</label>
                <input
                  type="text"
                  value={formData.iso_standard || ''}
                  onChange={(e) => setFormData({ ...formData, iso_standard: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  placeholder="e.g., ISO 9001"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Audit Plan Delivery (days)</label>
                  <input
                    type="number"
                    value={formData.audit_plan_delivery_days || ''}
                    onChange={(e) => setFormData({ ...formData, audit_plan_delivery_days: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Report Delivery (days)</label>
                  <input
                    type="number"
                    value={formData.report_delivery_days || ''}
                    onChange={(e) => setFormData({ ...formData, report_delivery_days: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Auditor Qualification</label>
                <input
                  type="text"
                  value={formData.auditor_qualification_level || ''}
                  onChange={(e) => setFormData({ ...formData, auditor_qualification_level: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            </>
          )}

          {/* Accreditation SLA Specific Fields */}
          {activeTab === 'accreditation' && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#333', fontWeight: 'bold' }}>
                  ISO Standards Accreditation
                </h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  Select which ISO standards the certification body is accredited to certify:
                </p>
                {isoStandards.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999', backgroundColor: '#f9f9f9', borderRadius: '4px', marginBottom: '10px' }}>
                    Loading ISO standards...
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '10px' }}>
                    {isoStandards.map(iso => (
                      <label
                        key={iso.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          backgroundColor: (formData.iso_standards_accredited || []).includes(iso.code) ? '#e7f3ff' : '#f9f9f9',
                          border: (formData.iso_standards_accredited || []).includes(iso.code) ? '2px solid #007bff' : '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = (formData.iso_standards_accredited || []).includes(iso.code) ? '#e7f3ff' : '#f9f9f9'}
                      >
                        <input
                          type="checkbox"
                          checked={(formData.iso_standards_accredited || []).includes(iso.code)}
                          onChange={() => toggleISOStandard(iso.code)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '14px', fontWeight: (formData.iso_standards_accredited || []).includes(iso.code) ? '600' : '400' }}>
                          {iso.code} - {iso.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                  Pricing: First 3 standards = £{pricingSettings?.iso_price_per_standard_first_3 || 500} each,
                  4-10 standards = £{pricingSettings?.iso_price_4_to_10_total || 1500} total
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#333', fontWeight: 'bold' }}>
                  Additional Services
                </h3>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    backgroundColor: formData.includes_cpd_certification ? '#e7f3ff' : '#f9f9f9',
                    border: formData.includes_cpd_certification ? '2px solid #007bff' : '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginBottom: '10px',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.includes_cpd_certification || false}
                    onChange={toggleCPD}
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: formData.includes_cpd_certification ? '600' : '500' }}>
                      CPD Certification
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Annual fee: £{pricingSettings?.cpd_certification_annual_fee || 1000}/year
                    </div>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px',
                    backgroundColor: formData.includes_company_assessments ? '#e7f3ff' : '#f9f9f9',
                    border: formData.includes_company_assessments ? '2px solid #007bff' : '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.includes_company_assessments || false}
                    onChange={toggleCompanyAssessments}
                    style={{ cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: formData.includes_company_assessments ? '600' : '500' }}>
                      Company Assessments
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Annual fee: £{pricingSettings?.company_assessments_annual_fee || 1000}/year
                    </div>
                  </div>
                </label>
              </div>

              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f8ff', border: '2px solid #007bff', borderRadius: '8px' }}>
                <div style={{ fontSize: '14px', color: '#333', marginBottom: '5px' }}>
                  <strong>Calculated Annual Fee:</strong>
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#007bff' }}>
                  £{formData.annual_fee?.toLocaleString() || '0'}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {(formData.iso_standards_accredited?.length || 0)} ISO standard(s) +
                  {formData.includes_cpd_certification ? ' CPD' : ''}{formData.includes_cpd_certification && formData.includes_company_assessments ? ' +' : ''}
                  {formData.includes_company_assessments ? ' Company Assessments' : ''}
                  {!formData.includes_cpd_certification && !formData.includes_company_assessments && (formData.iso_standards_accredited?.length || 0) === 0 ? ' (no services selected)' : ''}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Duration (days)</label>
                  <input
                    type="number"
                    value={formData.accreditation_duration_days || ''}
                    onChange={(e) => setFormData({ ...formData, accreditation_duration_days: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Audit Frequency (months)</label>
                  <input
                    type="number"
                    value={formData.audit_frequency_months || ''}
                    onChange={(e) => setFormData({ ...formData, audit_frequency_months: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Common Date Fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Effective Date *</label>
              <input
                type="date"
                value={formData.effective_date ? formData.effective_date.split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Expiration Date</label>
              <input
                type="date"
                value={formData.expiration_date ? formData.expiration_date.split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowCreateModal(false)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={creating}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateSLA}
              style={{
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: creating ? 'not-allowed' : 'pointer',
                opacity: creating ? 0.6 : 1
              }}
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create SLA'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading SLAs...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0 }}>SLA Management</h1>
          <p style={{ color: '#666', margin: '5px 0 0 0' }}>
            Manage Service Level Agreements for ICSC Platform Services, Reciprocal Audit Arrangements, and Accreditation Scope
          </p>
        </div>
        <button
          onClick={() => openCreateModal(activeTab)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px'
          }}
        >
          + Create {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} SLA
        </button>
      </div>

      {success && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid #c3e6cb'
        }}>
          {success}
          <button
            onClick={() => setSuccess('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#155724'
            }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
          <button
            onClick={() => setError('')}
            style={{
              float: 'right',
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              color: '#721c24'
            }}
          >
            ×
          </button>
        </div>
      )}

      <div style={{ marginBottom: '20px', borderBottom: '2px solid #dee2e6' }}>
        <button
          onClick={() => setActiveTab('provider')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'provider' ? '#007bff' : 'transparent',
            color: activeTab === 'provider' ? 'white' : '#007bff',
            border: 'none',
            borderBottom: activeTab === 'provider' ? '3px solid #007bff' : 'none',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Provider SLA ({BRAND.name}) ({slas.provider.length})
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'audit' ? '#007bff' : 'transparent',
            color: activeTab === 'audit' ? 'white' : '#007bff',
            border: 'none',
            borderBottom: activeTab === 'audit' ? '3px solid #007bff' : 'none',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Audit Service SLA ({slas.audit.length})
        </button>
        <button
          onClick={() => setActiveTab('accreditation')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'accreditation' ? '#007bff' : 'transparent',
            color: activeTab === 'accreditation' ? 'white' : '#007bff',
            border: 'none',
            borderBottom: activeTab === 'accreditation' ? '3px solid #007bff' : 'none',
            cursor: 'pointer'
          }}
        >
          Accreditation SLA (ICSC) ({slas.accreditation.length})
        </button>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        {activeTab === 'provider' && renderSLATable(slas.provider, 'provider')}
        {activeTab === 'audit' && renderSLATable(slas.audit, 'audit')}
        {activeTab === 'accreditation' && renderSLATable(slas.accreditation, 'accreditation')}
      </div>

      {renderCreateModal()}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#dc3545' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '30px', fontSize: '16px', lineHeight: '1.5' }}>
              Are you sure you want to delete this SLA? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingSLA(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteSLA}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Delete SLA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '900px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#007bff' }}>
                Version History - {selectedVersionSLA?.sla.title}
              </h2>
              <button
                onClick={closeVersionHistory}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>

            {loadingVersions ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                Loading version history...
              </div>
            ) : versionHistory.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                No version history available. Versions are created when an SLA is updated.
              </div>
            ) : (
              <>
                {!selectedVersion ? (
                  <>
                    <p style={{ marginBottom: '20px', color: '#666' }}>
                      Total versions: {versionHistory.length}
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Version</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Changed At</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Change Summary</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versionHistory.map((version) => (
                          <tr key={version.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '12px', fontWeight: 'bold' }}>
                              v{version.version_number}
                            </td>
                            <td style={{ padding: '12px' }}>
                              {new Date(version.created_at).toLocaleString()}
                            </td>
                            <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                              {version.change_summary || 'No summary provided'}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <button
                                onClick={() => viewVersion(version)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectedVersion(null)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginBottom: '20px',
                        fontSize: '12px'
                      }}
                    >
                      ← Back to Version List
                    </button>

                    <h3 style={{ marginBottom: '20px', color: '#333' }}>
                      Version {selectedVersion.version_number} Details
                    </h3>

                    <div style={{
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '4px',
                      marginBottom: '20px'
                    }}>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Changed:</strong> {new Date(selectedVersion.created_at).toLocaleString()}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '14px' }}>
                        <strong>Summary:</strong> {selectedVersion.change_summary || 'No summary provided'}
                      </p>
                    </div>

                    <div style={{
                      backgroundColor: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      padding: '20px',
                      maxHeight: '500px',
                      overflow: 'auto'
                    }}>
                      <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333' }}>SLA Data (Read-Only)</h4>
                      <table style={{ width: '100%', fontSize: '13px' }}>
                        <tbody>
                          {Object.entries(selectedVersion.data).map(([key, value]) => (
                            <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{
                                padding: '8px',
                                fontWeight: 'bold',
                                width: '30%',
                                color: '#555',
                                verticalAlign: 'top'
                              }}>
                                {key.split('_').map(word =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                                ).join(' ')}
                              </td>
                              <td style={{ padding: '8px', color: '#333' }}>
                                {typeof value === 'object'
                                  ? JSON.stringify(value, null, 2)
                                  : String(value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SLAManagement;
